<script lang="ts" setup>
import { Expand } from "@element-plus/icons-vue";
import { ElMessage, ElMessageBox } from "element-plus";
import {
  computed,
  nextTick,
  onMounted,
  onUnmounted,
  provide,
  ref,
  watch,
} from "vue";
import {
  FormConcurrencyMode,
  type FormDefinition,
  type FormElementBase,
  FormElementRule,
  FormElementType,
  FormRuleType,
  FormTextFormElement,
  FormType,
  LabelledOption,
  SectionAssignables,
  WorkflowDefinition,
  WorkflowEventTriggerType,
} from "~/api";
import { drawerWidth, emptyGuid } from "~/utils/constants";

import { apiClient } from "~/utils/apiClient";
import { getMessageTime, splitByCaps } from "~/utils/miscUtils";

import { HubConnection } from "@microsoft/signalr";
import { useRefHistory, useStorage } from "@vueuse/core";
import { onBeforeRouteLeave } from "vue-router";
import { type FormElementDefault } from "~/NewFormBuilder/components/FormComponents/defaultComponents";
import { getNewQuestionCode } from "~/utils/formUtils";
import { showError } from "~/utils/handleAPIError";
import { hubManager } from "~/utils/hubManager";
import { isFormElementBasedCondition } from "~/utils/typeGuards";
import { FormPage } from "../../composables/useFormResponse";
import FormComponentRenderer from "../FormComponents/FormComponentRenderer.vue";
import FormRendererInner from "../Renderer/FormRendererInner.vue";
import DroppablePad from "./DroppablePad.vue";
import FormDesignerPalette from "./FormDesignerPalette.vue";
import ElementEditor from "./FormEditors/ElementEditor.vue";
import FormPropertiesEditor from "./FormEditors/FormPropertiesEditor.vue";
import FormRule from "./FormEditors/FormRule.vue";
import FormRulesTable from "./FormEditors/FormRulesTable.vue";
import ListsTab from "./FormEditors/ListsTab.vue";
import TableOfContents from "./TableOfContents.vue";

import {
  dragDropInjectKey,
  useDragDrop,
} from "~/NewFormBuilder/composables/useDragDrop";
// import { Setting } from "@element-plus/icons-vue";

import FormJsonUploader from "./FormEditors/FormJsonUploader.vue";

import CompliancePalette from "~/Compliance/components/CompliancePalette.vue";
import { FormDisplayMode } from "~/NewFormBuilder/types/FormDisplayMode";
import { useMyInfoStore } from "~/stores/MyInfoStore";

/** Local copy of form from API, so we can edit the ref */
const form = defineModel<FormDefinition>({ required: true });
/** The guid of the component we are currently editing the properties of. Don't edit this directly, use the 'editingComponent' instead. */
const _editingComponentGuid = ref<string | undefined>();
/** The component we are currently editing the properties of */
const editingComponent = computed<FormElementBase | undefined>({
  get: () =>
    form.value.components.find((c) => c.guid === _editingComponentGuid.value),

  set: (val) => (_editingComponentGuid.value = val?.guid),
});
/** Currently displayed page number of the form. */
const currentPageNumber = ref<number>(1);
/** All pages of the form, synced from the FormRendererInner component. Used here for the TOC */
const formPages = ref<FormPage[]>([]);
/** Last time the save changes was called. */
const lastSaveTime = ref<Date>(new Date());
/** True if we are currently dragging around a component. */
const isDragging = ref<boolean>(false);
/** True if we can drop this in a repeater component. */
const canDropInRepeater = ref<boolean>(true);
/** True if we are currently adding/editing form rules. */
const isRuleDrawerOpen = ref<boolean>(false);
/** True if the form properties editor is open */
// const isFormPropertiesEdit = ref<boolean>(false);
/** True if we want to display question codes above each element. Persisted in local storage */
const showQuestionCodes = useStorage("show-question-codes", false);
const autoSaveForm = useStorage("auto-save-form", false);
const splitFormPages = useStorage("split-form-pages", false);
const applyRules = useStorage("apply-rules", false);
const showArchived = useStorage("show-archived", false);

const formUploaderVisible = ref<boolean>(false);
/** Need open counter so that :key is different every time */
const uploaderOpenCounter = ref(0);

/** The tab that is currently active - remembered in sessionStorage */
const activeTab = useStorage("formDesignerActiveTab", "design", sessionStorage);
const activeElementEditorTab = ref<string>("properties");

/** Number of archived components */
const numberArchived = computed<number>(
  () => form.value.components.filter((c) => c.isArchived).length,
);

/** Keeps track of the form's history */
const history = useRefHistory(form, {
  deep: true,
  flush: "sync",
  capacity: 10,
});

/** called when a save is needed */
const emit = defineEmits<{
  (e: "saveNeeded", saveNeeded: boolean): boolean;
  (e: "formSaved", form: FormDefinition): FormDefinition;
  (e: "closeDrawer"): void;
  (e: "deleted", form: FormDefinition): void;
}>();

const props = defineProps<{
  workflowDefinition?: WorkflowDefinition;
  complianceItem?: boolean;
  drawerClosed?: boolean;
}>();

const handleDeleteFormAndResponses = async (formDefinitionGuid: string) => {
  try {
    await ElMessageBox.confirm(
      `This form has ${form.value.formResponseCount} associated form responses. Are you sure you want to delete this form and all its responses?`,
      "Delete Form",
      {
        confirmButtonText: "Delete",
        cancelButtonText: "Cancel",
        closeOnPressEscape: false,
        closeOnClickModal: false,
        showClose: false,
        type: "warning",
      },
    );
    if (!formDefinitionGuid) {
      return;
    }
    try {
      const res =
        await apiClient.formDefinition.deleteFormDefinition(formDefinitionGuid);
      ElMessage.success("Form and associated responses have been deleted");
      emit("closeDrawer");
      emit("saveNeeded", false);
      emit("deleted", res);
    } catch (error) {
      showError(error);
    }
  } catch {
    // do nothing
  }
};
const complianceItem = ref(props.complianceItem);
const isDrawerClosed = computed(() => props.drawerClosed);
const isDrawerOpen = computed(() => !isDrawerClosed.value);

/** List of elements that can't be deleted */
const elementsThatCantBeDeleted = computed<string[]>(() => {
  // if there is only one section, don't allow it to be deleted
  const sections = form.value.components.filter(
    (c) => c.type === FormElementType.SECTION,
  );
  let result: string[] = [];

  if (sections.length === 1) {
    result.push(sections[0].guid);
  }

  const sectionsWithComponents = sections
    .map((s) => s.guid)
    .filter((guid) => form.value.components.some((c) => c.parentGuid === guid));
  result = result.concat(sectionsWithComponents);

  return result;
});

/** Whether any edits have been made and a save will be needed to persist the update to the DB */
const saveNeeded = computed<boolean>(() => history.history.value.length > 1);

const handleClickComponent = (
  component: FormElementBase,
  event?: MouseEvent,
) => {
  // prevents the click from propagating to the parent div when we click on an element in a repeater
  event?.stopPropagation();
  const eventElements = [
    FormElementType.NUMBER,
    FormElementType.MONEY,
    FormElementType.SIMPLE_TEXT,
    FormElementType.RADIO_BUTTONS,
    FormElementType.CHECKBOXES,
  ];
  //Handle active tab changes when the element does not contain the tab.
  if (
    editingComponent.value &&
    ((editingComponent.value.type === FormElementType.SECTION &&
      component.type !== FormElementType.SECTION) ||
      (eventElements.includes(editingComponent.value.type) &&
        !eventElements.includes(component.type))) &&
    activeElementEditorTab.value === "events"
  ) {
    activeElementEditorTab.value = "properties";
  }
  if (
    editingComponent.value?.type === FormElementType.SECTION &&
    component.type !== FormElementType.SECTION &&
    activeElementEditorTab.value === "Access"
  ) {
    activeElementEditorTab.value = "properties";
  }

  editingComponent.value = component;
};

/**
 * This function takes in the section guid and its index in the form components array.
 * It updates the parent guid of all the items after the dropped section until the element with the same parent guid (aka the next Section).
 * @param sectionGuid The guid of the section that was dropped
 * @param sectionIndex The index of the section that was dropped
 */
const handleUpdateSectionChildren = async (
  sectionGuid: string,
  sectionIndex: number,
) => {
  const formGuid = form.value.guid;
  // get all the items after the dropped section until the element with the same parent guid
  let itemsToUpdateParentGuid = form.value.components.slice(sectionIndex + 1);

  for (let i = 0; i < itemsToUpdateParentGuid.length; i++) {
    if (itemsToUpdateParentGuid[i].parentGuid === formGuid) {
      itemsToUpdateParentGuid = itemsToUpdateParentGuid.slice(0, i);
      break;
    }
  }

  // update the parent guid of all the items after the dropped section until the next section
  const updateItemParentGuidProms: Promise<FormElementBase>[] =
    itemsToUpdateParentGuid.map(async (item) => {
      item.parentGuid = sectionGuid;
      if (!item.guid) {
        return new Promise(() => item);
      }
      return apiClient.formElement.updateFormElement(
        form.value.guid,
        item.guid,
        item,
      ) as Promise<FormElementBase>;
    });
  await Promise.all(updateItemParentGuidProms).catch((err) => {
    showError("Error updating child Guids");
    console.log(err);
  });
};

const handleDropComponent = async (e: DragEvent, repeaterGuid?: string) => {
  if (!hasSetupBasicRole || !canEditForm.value) {
    e.preventDefault();
    return;
  }

  // we get the component from the pending component if it is set
  let droppedComponent = pendingComponent.value;
  pendingComponent.value = null;

  // if not, we get it from the dataTransfer
  if (droppedComponent === null) {
    const droppedComponentString = e.dataTransfer?.getData("text/plain") ?? "";
    droppedComponent = JSON.parse(droppedComponentString);
  }

  if (!droppedComponent) {
    showError("No pending component set");
    return;
  }

  const componentAboveId = (e.target as HTMLDivElement)?.id;
  // Id may be guid or questionCode

  let droppedIndex = form.value.components.findIndex(
    (c) => c.guid === componentAboveId,
  );

  if (droppedIndex === -1) {
    droppedIndex = form.value.components.findIndex(
      (c) => c.questionCode === componentAboveId,
    );
  }

  // handle new section
  if (
    droppedComponent.type === FormElementType.SECTION &&
    !droppedComponent.formDefinitionGuid
  ) {
    droppedComponent.formDefinitionGuid = form.value.guid;
    droppedComponent.parentGuid = form.value.guid;
    droppedComponent.questionCode = getNewQuestionCode(
      form.value,
      FormElementType.SECTION,
    );
    droppedComponent.label = splitByCaps(droppedComponent.questionCode);
    // @ts-expect-error: Force the server to generate the Guid
    droppedComponent.guid = undefined;

    await apiClient.formElement
      .createFormElement(form.value.guid, droppedComponent)
      .then((formElement) => {
        droppedComponent.guid = formElement.guid;
        form.value.components.splice(droppedIndex + 1, 0, formElement);
        ElMessage.success("Section added to workflow");
      })
      .catch(showError);

    editingComponent.value = form.value.components[droppedIndex + 1];

    // if the section was dropped in between another section
    if (
      form.value.components[droppedIndex + 2]?.parentGuid &&
      form.value.components[droppedIndex + 2]?.parentGuid !==
        droppedComponent.parentGuid
    ) {
      handleUpdateSectionChildren(droppedComponent.guid, droppedIndex + 1);
    }

    await handleSaveChanges();
    return;
  }

  if (
    droppedComponent.type === FormElementType.REPEATER &&
    !droppedComponent.formDefinitionGuid
  ) {
    droppedComponent.formDefinitionGuid = form.value.guid;
    droppedComponent.parentGuid = form.value.guid;
    droppedComponent.questionCode = getNewQuestionCode(
      form.value,
      FormElementType.REPEATER,
    );
    droppedComponent.label = splitByCaps(droppedComponent.questionCode);
    // @ts-expect-error: Force the server to generate the Guid
    droppedComponent.guid = undefined;

    await apiClient.formElement
      .createFormElement(form.value.guid, droppedComponent)
      .then((res) => {
        droppedComponent.guid = res.guid;
        form.value.components.splice(droppedIndex + 1, 0, res);
        ElMessage.success("Repeater added to workflow");
      })
      .catch(showError);

    editingComponent.value = form.value.components[droppedIndex + 1];
    await handleSaveChanges();
    return;
  }

  if (
    droppedComponent.type === FormElementType.ENTITY ||
    droppedComponent.type === FormElementType.WORKFLOW_ASSOCIATION
  ) {
    const allEntityDefinitions =
      await apiClient.entityDefinition.getAllEntityDefinitionDtos(
        myInfoStore.currentSysOrgGuid,
      );
    entityDefinitions.value = allEntityDefinitions.map((f) => ({
      label: f.name ?? "",
      value: f.guid ?? "",
    }));
  }

  if (droppedComponent.type === FormElementType.SECURITY_GROUP) {
    const allSecurityGroups = await apiClient.orgGroup.getAllOrgGroupDtos(
      myInfoStore.currentSysOrgGuid,
    );
    securityGroups.value = allSecurityGroups.map((f) => ({
      label: f.name ?? "",
      value: f.guid ?? "",
    }));
  }

  if (droppedComponent.type === FormElementType.LINKED_ANSWER) {
    const allForms = await apiClient.formDefinition.getAllFormDefinitions();
    formDefinitionsList.value = allForms.map((f) => ({
      label: f.name,
      value: f.guid,
    }));
  }

  // the parent guid of this component is the guid of the first section above it
  const parentGuid =
    repeaterGuid ??
    form.value.components
      .slice(0, droppedIndex + 1)
      .reverse()
      .find((c) => c.type === FormElementType.SECTION)?.guid;

  // if there is no section above it, then don't allow the drop
  if (!parentGuid && droppedComponent.type !== FormElementType.SECTION) {
    showError("A form item must be added to a Section.");
    return;
  }

  if (e.dataTransfer?.effectAllowed === "copy") {
    const newElement: FormElementBase = {
      type: droppedComponent.type,
      label: droppedComponent.label,
      properties: JSON.parse(JSON.stringify(droppedComponent.properties || {})),
      events: [],
      formDefinitionGuid: form.value.guid,
      // @ts-expect-error: Force the server to generate the Guid
      parentGuid: parentGuid,
      questionCode: getNewQuestionCode(form.value, droppedComponent.type),
      // @ts-expect-error: Force the server to generate the Guid
      guid: undefined, // force server to generate the guid
    };

    // if it is a compliance item, don't change the label
    newElement.label =
      newElement.type === FormElementType.COMPLIANCE_ITEM
        ? newElement.label
        : splitByCaps(newElement.questionCode);

    form.value.components.splice(droppedIndex + 1, 0, newElement);

    editingComponent.value = newElement;
  } else if (e.dataTransfer?.effectAllowed === "move") {
    // get the current index of the component that was dropped
    const currentIndex = form.value.components.findIndex(
      // assume questions have been saved and have guids
      (c) => c.guid === droppedComponent.guid,
    );

    // When you drag from a greater index to a smaller index, the droppedIndex
    // needs to be incremented by 1 because the target index is affected by the
    // shift caused by the removal of the component
    if (!repeaterGuid && droppedIndex < currentIndex) {
      droppedIndex++;
    }

    // if the dropped component is a section, move the section and its children
    if (droppedComponent.type === FormElementType.SECTION) {
      const sectionChildren = form.value.components.filter(
        (c) => c.parentGuid === droppedComponent.guid,
      );

      // remove the section and its children from the list
      form.value.components = form.value.components.filter(
        (c) => c.guid !== droppedComponent.guid && !sectionChildren.includes(c),
      );

      // add the section and its children to the new location
      form.value.components.splice(
        droppedIndex,
        0,
        droppedComponent,
        ...sectionChildren,
      );
    }
    // otherwise, move the component
    else {
      // update the parent guid of the dropped component in case it was moved to a different section
      // @ts-expect-error: Force the server to generate the Guid
      droppedComponent.parentGuid = parentGuid;

      // remove the dropped component from the list
      form.value.components = form.value.components.filter(
        (c) => c.questionCode !== droppedComponent.questionCode,
      );

      // add it to its new location
      form.value.components.splice(droppedIndex, 0, droppedComponent);
    }
  } else {
    showError(
      "`effectAllowed` on component drop has no handler: " +
        e.dataTransfer?.effectAllowed,
    );
  }

  pendingComponent.value = null;
};

const handleOnDrag = (e: DragEvent, component: FormElementBase) => {
  e.stopPropagation();

  if (!hasSetupBasicRole || !canEditForm.value) {
    e.preventDefault();
    if (e.dataTransfer) {
      e.dataTransfer.effectAllowed = "none";
    }
    return;
  }

  // if the component is the first Page Break in a Section, don't allow it to be moved
  if (
    component.type === FormElementType.HEADING &&
    form.value.components[form.value.components.indexOf(component) - 1]
      ?.type === FormElementType.SECTION
  ) {
    e.dataTransfer?.setDragImage(new Image(), 0, 0);
    ElMessage.warning("This component cannot be moved");
    return;
  }

  // if the component is a repeater, don't allow it to be dropped in another repeater
  if (component.type === FormElementType.REPEATER) {
    canDropInRepeater.value = false;
  } else {
    canDropInRepeater.value = true;
  }

  // To fix chrome bug. See:
  // https://stackoverflow.com/questions/14203734/dragend-dragenter-and-dragleave-firing-off-immediately-when-i-drag#20733870
  setTimeout(() => {
    isDragging.value = e.type === "dragstart";
  }, 10);

  if (e.dataTransfer) {
    e.dataTransfer.effectAllowed = "move";
    pendingComponent.value = JSON.parse(JSON.stringify(component));
    canDropInRepeater.value = component.type !== FormElementType.REPEATER;
    // can't use dataTransfer.setData() because the tinyMCE editor will accept it and we don't want to drop there
    // e.dataTransfer.setData("text/plain", JSON.stringify(component));
  }
};

const dragDropState = useDragDrop({
  isDragging,
  handleDropComponent,
  handleClickComponent,
  handleOnDrag,
  editingComponent,
  canDropInRepeater,
});
provide(dragDropInjectKey, dragDropState);

const adjustSavedValues = (form: FormDefinition) => {
  form.components.forEach((c) => {
    switch (c.type) {
      case FormElementType.FORM_TEXT: {
        // the tinyMCE editor sometimes adds a trailing blank line, especially after an <hr>, which we don't want to save
        const trailingBlankLine = "\n<p>&nbsp;</p>";
        const el: FormTextFormElement = c as FormTextFormElement;
        let text = el.properties.content ?? "";
        let changeCount = 0;
        while (text.endsWith(trailingBlankLine)) {
          text = text.slice(0, 0 - trailingBlankLine.length);
          changeCount++;
        }
        if (changeCount) {
          // replace it
          el.properties.content = text;
        }
        break;
      }
    }
  });
  return form;
};

const refreshFormDefinition = async (updateInfo: any) => {
  const formattedTime = new Date(updateInfo.updatedAt).toLocaleTimeString();

  if (updateInfo.updatedByUser !== myInfoStore.userInfo?.loginName) {
    const txt = `The form has been updated by ${updateInfo.updatedByUser} at ${formattedTime}. Refreshing form definition...`;
    const msg = ElMessage({
      message: txt,
      type: "info",
      duration: getMessageTime(txt),
    });

    try {
      const updatedForm = await apiClient.formDefinition.getFormDefinition(
        updateInfo.formDefinitionGuid,
      );
      if (updatedForm) {
        msg.close();
        form.value = updatedForm;
        // Clear the history after form is updated from another user
        nextTick(() => history.clear());
        const txt2 = `The form has been updated.`;
        ElMessage({
          message: txt2,
          type: "success",
          duration: getMessageTime(txt2),
        });
      }
    } catch (error) {
      showError(
        "Failed to refresh form definition. Please try refreshing the page manually.",
        error,
      );
    }
  }
};

const handleCancelChanges = async () => {
  if (history.history.value.length > 1) {
    try {
      await ElMessageBox.confirm(
        "Are you sure you want to discard your changes?",
        "Discard Changes",
        {
          cancelButtonText: "Continue Editing",
          confirmButtonText: "Discard",
          closeOnPressEscape: false,
          type: "warning",
        },
      );
    } catch {
      return;
    }
    if (form.value.guid) {
      try {
        const latestForm = await apiClient.formDefinition.getFormDefinition(
          form.value.guid,
        );
        if (latestForm) {
          form.value.components = latestForm.components;
        }
      } catch (err) {
        showError("Failed to reload form from server.", err);
      }
    }
    history.clear();
  }
};

const handleSaveChanges = async () => {
  clearTimeout(saveChangesId.value);
  // are there any component with the same question code?
  const questionCodes = form.value.components.map((c) => c.questionCode);
  const duplicates = questionCodes.filter(
    (code, index) => questionCodes.indexOf(code) !== index,
  );
  if (duplicates.length > 0) {
    showError(
      `There are duplicate question codes: ${duplicates.join(", ")}. Please fix these before saving.`,
    );
    return;
  }

  await apiClient.formDefinition
    .updateFormDefinition(form.value.guid, adjustSavedValues(form.value))
    .then((res) => {
      justReturned.value = true;
      if (!formDesignerHub) {
        emit("saveNeeded", false);
        return;
      }

      form.value = res;
      // Reset the guid to the server returned one if we have a new element selected
      if (_editingComponentGuid.value === undefined) {
        _editingComponentGuid.value = res.components.find(
          (c) => c.questionCode === editingComponent.value?.questionCode,
        )?.guid;
      }

      nextTick(() => history.clear());
      const txt = "Saved";
      ElMessage.success({
        message: txt,
        grouping: true,
        duration: getMessageTime(txt),
      });
      lastSaveTime.value = new Date();
      emit("formSaved", res);
      emit("saveNeeded", false);
    })
    .catch(showError);
};

const handleStopEditing = () => {
  editingComponent.value = undefined;
};

const handleDeleteElementFromWorkflow = async (element: FormElementBase) => {
  const referencedRules: FormElementRule[] = form.value.rules.filter(
    (r) =>
      r.elementQuestionCode === element.questionCode ||
      r.conditions.some((c) =>
        isFormElementBasedCondition(c)
          ? c.questionCode === element.questionCode
          : false,
      ),
  );

  let deleteMessage: string;
  if (referencedRules.length > 0) {
    deleteMessage = `This will permanently delete this element, any answers associated with it, and any rules/conditions that reference it. Continue?`;
  } else {
    deleteMessage = `This will permanently delete this element and any answers associated with it. Continue?`;
  }

  const title = `Deleting: ${element.label}`;

  try {
    await ElMessageBox.confirm(deleteMessage, title, {
      confirmButtonText: "OK",
      cancelButtonText: "Cancel",
      type: "warning",
    });

    editingComponent.value = undefined;

    // Remove rules referencing this element
    let rules = form.value.rules;
    if (referencedRules.length > 0) {
      rules = rules.filter(
        (r) => r.elementQuestionCode !== element.questionCode,
      );
      rules = rules.map((r) => ({
        ...r,
        conditions: r.conditions.filter((c) =>
          isFormElementBasedCondition(c)
            ? c.questionCode !== element.questionCode
            : true,
        ),
      }));
      rules = rules.filter((r) => r.conditions.length !== 0);
    }
    form.value.rules = rules;

    // Actually delete the element and its answers
    if (element.guid) {
      try {
        await apiClient.formElement.deleteFormElement(
          element.guid,
          form.value.guid,
        );
      } catch (error) {
        showError("Failed to delete element and answers from server.", error);
        return;
      }
    }

    // Remove from local components
    form.value.components = form.value.components.filter(
      (c) => c.guid !== element.guid,
    );

    ElMessage.warning({
      message: `${element.questionCode} and its answers will be deleted on next Save`,
    });
  } catch {
    // Cancelled
  }
};

const handleArchiveElementFromWorkflow = async (element: FormElementBase) => {
  const referencedRules: FormElementRule[] = form.value.rules.filter(
    (r) =>
      r.elementQuestionCode === element.questionCode ||
      // r.conditions.some((c) => c.questionCode === element.questionCode),
      r.conditions.some((c) =>
        isFormElementBasedCondition(c)
          ? c.questionCode === element.questionCode
          : false,
      ),
  );

  let archiveMessage: string;
  if (referencedRules.length > 0) {
    archiveMessage = `This will archive this element and any rules/conditions that reference it. Continue?`;
  } else {
    archiveMessage = `This will archive this element. Continue?`;
  }

  if (responsesExist.value) {
    archiveMessage =
      "This will archive this element and it will no longer show in any responses. Continue?";
  }

  const title = `Archiving: ${element.label}`;

  ElMessageBox.confirm(archiveMessage, title, {
    confirmButtonText: "OK",
    cancelButtonText: "Cancel",
    type: "warning",
  }).then(() => {
    editingComponent.value = undefined;

    // if the element has responses, we archive it
    if (!responsesExist.value) {
      // If the component has a guid, it has been saved to the db, so we should delete it there
      let rules = form.value.rules;
      if (referencedRules.length > 0) {
        // Delete rules that directly reference this element
        rules = rules.filter(
          (r) => r.elementQuestionCode !== element.questionCode,
        );
        // Remove all conditions that directly reference this element
        rules = rules.map((r) => ({
          ...r,
          conditions: r.conditions.filter((c) =>
            isFormElementBasedCondition(c)
              ? c.questionCode !== element.questionCode
              : true,
          ),
        }));
        // Remove all rules that no longer have any conditions
        rules = rules.filter((r) => r.conditions.length !== 0);
      }

      form.value.rules = rules;
      form.value.components = form.value.components.filter(
        (c) => c !== element,
      );
    } else {
      element.isArchived = true;
      form.value.components = form.value.components.map((c) =>
        c.guid === element.guid ? element : c,
      );
    }
    ElMessage.warning({
      message: `${element.questionCode} will be ${responsesExist.value ? "archived" : "deleted"} on next Save`,
    });
  });
};

const handleUnarchiveElementFromWorkflow = async (element: FormElementBase) => {
  const msg =
    "This will unarchive this element and it will now appear in any responses. Continue?";

  ElMessageBox.confirm(msg, `Unarchiving: ${element.label}`, {
    confirmButtonText: "OK",
    cancelButtonText: "Cancel",
    type: "warning",
  }).then(() => {
    editingComponent.value = undefined;
    element.isArchived = false;
    form.value.components = form.value.components.map((c) =>
      c.guid === element.guid ? element : c,
    );
    ElMessage.warning({
      message: `${element.questionCode} will be unarchived on next Save`,
    });
  });
};

const handleOpenRuleDrawer = () => {
  isRuleDrawerOpen.value = true;
};
const handleCloseRuleDrawer = () => {
  isRuleDrawerOpen.value = false;
};

/**
 * Updates the form when the FormPropertiesEditorModal is saved
 * @param updated The updated form
 */
const handleUpdateFormProperties = (updated: FormDefinition) => {
  form.value = updated;
  // Clear the current history so we don't prompt for another save
  nextTick(history.clear);
};

const confirmLeave = async (): Promise<boolean> => {
  return ElMessageBox.confirm("You have unsaved changes.", "Unsaved Changes", {
    cancelButtonText: "Discard",
    confirmButtonText: "Continue Editing",
    closeOnPressEscape: false,
    type: "warning",
  })
    .then(() => {
      return false; // don't close
    })
    .catch(() => {
      return true;
    });
};

// Invoke the LeaveGroup method from the server to leave the group and update userlist
const leaveFormGroup = () => {
  if (formDesignerHub && formDesignerHub.state === "Connected") {
    try {
      formDesignerHub.invoke("LeaveGroup", form.value.guid);
      if (myInfoStore.userInfo) {
        ElMessage.closeAll();
      }
    } catch (error) {
      showError("Error leaving form group:", error);
    }
  }
};

onBeforeRouteLeave(async () => {
  let confirm = true;
  if (saveNeeded.value) {
    confirm = await confirmLeave();
  }
  return confirm;
});

// Save form automatically, debounced with a delay of 5000ms
const saveChangesId = ref<ReturnType<typeof setTimeout> | undefined>(undefined);
const justReturned = ref<boolean>(false);
watch(
  () => form,
  async () => {
    if (!autoSaveForm.value) {
      return;
    }
    if (justReturned.value) {
      justReturned.value = false;
      return;
    }

    if (saveChangesId.value !== undefined) {
      clearTimeout(saveChangesId.value);
    }
    saveChangesId.value = setTimeout(() => handleSaveChanges(), 5000);
  },
  { deep: true },
);

watch(
  () => saveNeeded.value,
  (saveNeeded) => {
    emit("saveNeeded", saveNeeded);
  },
);

const securityGroups = ref<LabelledOption[]>([]);
const entityDefinitions = ref<LabelledOption[]>([]);
const formDefinitionsList = ref<LabelledOption[]>([]);
const listDefinitionsList = ref<LabelledOption[]>([]);
const eventCodesList = ref<LabelledOption[]>([]);

const myInfoStore = useMyInfoStore();
const sectionAssignables = ref<SectionAssignables>({});
const responsesExist = ref<boolean>();

const hasSetupBasicRole = myInfoStore.hasSetupBasicRole;

let formDesignerHub: HubConnection | null;

onMounted(async () => {
  // console.log("FormDesignerPage mounted", { drawerClosed: props.drawerClosed });

  // Check if drawer is already closed on mount and call leaveFormGroup if needed
  if (props.drawerClosed) {
    leaveFormGroup();
  }

  // get the list here... could do at a higher level, but not too bad here
  const extras = await apiClient.formDesignerExtras.getFormDesignerExtras(
    myInfoStore.currentSysOrgGuid,
  );
  sectionAssignables.value = extras.sectionAssignables;
  responsesExist.value = await apiClient.formResponses.hasResponses(
    emptyGuid,
    form.value.guid,
  );
  window.addEventListener("keydown", handleKeyDown);

  if (
    form.value.components.some(
      (c) =>
        c.type === FormElementType.ENTITY ||
        c.type === FormElementType.WORKFLOW_ASSOCIATION,
    )
  ) {
    const allEntityDefinitions =
      await apiClient.entityDefinition.getAllEntityDefinitionDtos(
        myInfoStore.currentSysOrgGuid,
      );
    entityDefinitions.value = allEntityDefinitions.map((f) => ({
      label: f.name ?? "",
      value: f.guid ?? "",
    }));
  }

  if (
    form.value.components.some((c) => c.type === FormElementType.SECURITY_GROUP)
  ) {
    const allSecurityGroups = await apiClient.orgGroup.getAllOrgGroupDtos(
      myInfoStore.currentSysOrgGuid,
    );

    securityGroups.value = allSecurityGroups.map((f) => ({
      label: f.name ?? "",
      value: f.guid ?? "",
    }));
  }

  if (
    form.value.components.some((c) => c.type === FormElementType.LINKED_ANSWER)
  ) {
    const allForms = await apiClient.formDefinition.getAllFormDefinitions();
    formDefinitionsList.value = allForms.map((f) => ({
      label: f.name,
      value: f.guid,
    }));
  }

  const allLists = await apiClient.listDefinition.getPdfCodeListDefinitions(
    props.workflowDefinition?.guid,
    myInfoStore.currentSysOrgGuid,
  );
  listDefinitionsList.value = allLists.flatMap((f) =>
    f.items.map((i) => ({
      label: i.label,
      value: i.value,
    })),
  );

  eventCodesList.value =
    props.workflowDefinition?.statusChangeActionsList
      .filter(
        (action) => action.triggerType === WorkflowEventTriggerType.ACTION_CODE,
      )
      .map((action) => ({
        label: action.eventCode ?? "",
        value: action.eventCode ?? "",
      })) || [];
});

onUnmounted(() => {
  window.removeEventListener("keydown", handleKeyDown);
  clearTimeout(hubResponseTimeout.value);

  leaveFormGroup();
  // Use handler refs (not bare off) to avoid stripping listeners from other "formdesigner" hub users.
  formDesignerHub?.off("FormEditors", handleFormDesignerHubMessage);
  formDesignerHub?.off("FormUpdated", refreshFormDefinition);
  formDesignerHub?.off("ControlRequestReceived", handleControlRequestReceived);
  formDesignerHub?.off("ControlRequestResponse", handleControlRequestResponse);
  formDesignerHub?.off("JoinGroupError", handleJoinGroupError);

  // clear the list definitions from session storage since they are form-specific
  sessionStorage.removeItem("listDefinitions");
});

// Leave the group when the drawer closes
watch(
  isDrawerClosed,
  (val) => {
    if (val) {
      clearTimeout(hubResponseTimeout.value);
      leaveFormGroup();
      formDesignerHub?.off("FormEditors", handleFormDesignerHubMessage);
      formDesignerHub?.off("FormUpdated", refreshFormDefinition);
      formDesignerHub?.off(
        "ControlRequestReceived",
        handleControlRequestReceived,
      );
      formDesignerHub?.off(
        "ControlRequestResponse",
        handleControlRequestResponse,
      );
      formDesignerHub?.off("JoinGroupError", handleJoinGroupError);
      if (myInfoStore.userInfo) {
        ElMessage.closeAll();
      }
    }
  },
  { immediate: true },
);

watch(
  isDrawerOpen,
  async (val) => {
    if (!val) return;

    formDesignerHub = await hubManager.getHub("formdesigner", "/formdesigner");
    if (!formDesignerHub) {
      // Hub failed to connect — allow editing based on role
      canEditForm.value = hasSetupBasicRole;
      return;
    }

    // Off first using stable refs (in case of re-open without full unmount) — only our listeners.
    formDesignerHub.off("FormEditors", handleFormDesignerHubMessage);
    formDesignerHub.off("FormUpdated", refreshFormDefinition);
    formDesignerHub.off("ControlRequestReceived", handleControlRequestReceived);
    formDesignerHub.off("ControlRequestResponse", handleControlRequestResponse);
    formDesignerHub.off("JoinGroupError", handleJoinGroupError);

    formDesignerHub.on("FormEditors", handleFormDesignerHubMessage);
    formDesignerHub.on("FormUpdated", refreshFormDefinition);
    formDesignerHub.on("ControlRequestReceived", handleControlRequestReceived);
    formDesignerHub.on("ControlRequestResponse", handleControlRequestResponse);
    formDesignerHub.on("JoinGroupError", handleJoinGroupError);

    try {
      await formDesignerHub.invoke("JoinGroup", form.value.guid);
      // If no FormEditors response within 5s, fall back to role-based editing
      hubResponseTimeout.value = setTimeout(() => {
        if (!canEditForm.value && hasSetupBasicRole) {
          canEditForm.value = true;
          console.warn(
            "FormDesigner: No hub response received, enabling edit mode",
          );
        }
      }, 5000);
    } catch (error) {
      showError("Error joining form group:", error);
      canEditForm.value = hasSetupBasicRole;
    }
  },
  { immediate: true },
);

const canEditForm = ref<boolean>(false);
const controlRequestPending = ref(false);
/** Timeout ID for the hub response fallback */
const hubResponseTimeout = ref<ReturnType<typeof setTimeout> | undefined>(
  undefined,
);

const concurrencyMode = computed(
  () =>
    form.value.metaInfo.concurrencyMode ?? FormConcurrencyMode.FIRST_IN_WINS,
);

const handleJoinGroupError = () => {
  canEditForm.value = hasSetupBasicRole;
};

const handleFormDesignerHubMessage = (formDesignerUsers: any) => {
  // Hub responded — clear the fallback timeout
  clearTimeout(hubResponseTimeout.value);

  const users = formDesignerUsers.users;
  if (!users || users.length === 0) {
    canEditForm.value = hasSetupBasicRole;
    return;
  }

  const currentUserName = myInfoStore.userInfo?.loginName;
  const currentUser = users.find((u: any) => u.userName === currentUserName);
  const otherUsers = users.filter((u: any) => u.userName !== currentUserName);
  const otherUserNames = otherUsers.map((u: any) => u.userName);

  // No other users editing
  if (otherUsers.length === 0) {
    canEditForm.value = hasSetupBasicRole;
    controlRequestPending.value = false;
    ElMessage.closeAll();
    return;
  }

  if (!currentUser?.joinedAt) return;

  // Determine if current user can edit based on concurrency mode
  if (hasSetupBasicRole) {
    switch (concurrencyMode.value) {
      case FormConcurrencyMode.READ_ONLY_WHEN_SHARED:
        canEditForm.value = false;
        break;
      case FormConcurrencyMode.ALLOW_CONCURRENT:
        canEditForm.value = true;
        break;
      case FormConcurrencyMode.REQUEST_CONTROL:
      case FormConcurrencyMode.FIRST_IN_WINS:
      default: {
        const editorsWithPermission = otherUsers.filter((u: any) => u.canEdit);
        if (editorsWithPermission.length === 0) {
          canEditForm.value = true;
        } else {
          const currentJoined = new Date(currentUser.joinedAt);
          const isEarliest = editorsWithPermission.every(
            (u: any) => !u.joinedAt || new Date(u.joinedAt) > currentJoined,
          );
          canEditForm.value = isEarliest;
        }
        break;
      }
    }
  } else {
    canEditForm.value = false;
  }

  // Reset pending request if we gained control
  if (canEditForm.value) {
    controlRequestPending.value = false;
  }

  ElMessage.closeAll(); // clear previous messages

  // Build status message based on concurrency mode
  let message = "";
  let messageType: "warning" | "info" = "warning";

  if (concurrencyMode.value === FormConcurrencyMode.READ_ONLY_WHEN_SHARED) {
    message = buildOtherUsersMessage(otherUserNames);
    message += ". Form is read-only while other users are present";
    messageType = "info";
  } else if (concurrencyMode.value === FormConcurrencyMode.ALLOW_CONCURRENT) {
    message = buildOtherUsersMessage(otherUserNames);
    message += ". Concurrent editing is enabled";
    messageType = "info";
  } else {
    // First-in-wins or Request Control
    const usersWithEdit = users.filter((u: any) => u.canEdit);
    const eligibleUsers = usersWithEdit.length > 0 ? usersWithEdit : users;

    const editor = eligibleUsers.reduce((earliest: any, u: any) => {
      return !earliest ||
        (u.joinedAt && new Date(u.joinedAt) < new Date(earliest.joinedAt))
        ? u
        : earliest;
    }, null);

    if (editor?.userName === currentUserName) {
      message = buildOtherUsersMessage(otherUserNames);
    } else if (editor) {
      const others = users.filter(
        (u: any) =>
          u.userName !== currentUserName && u.userName !== editor.userName,
      );
      const otherNames = others.map((u: any) => u.userName);
      const action = editor.canEdit ? "editing" : "viewing";

      message = `${editor.userName} is ${action} this form`;
      if (others.length === 1) {
        message += `. ${otherNames[0]} is also viewing the form`;
      } else if (others.length === 2) {
        message += `. ${otherNames[0]} and ${otherNames[1]} are also viewing the form`;
      } else if (others.length > 2) {
        const last = otherNames.pop();
        message += `. ${otherNames.join(", ")} and ${last} are also viewing the form`;
      }

      if (
        concurrencyMode.value === FormConcurrencyMode.REQUEST_CONTROL &&
        hasSetupBasicRole &&
        !canEditForm.value
      ) {
        message += ". Click 'Request Control' to request edit access";
      }
    }
  }

  ElMessage({
    message,
    type: messageType,
    duration: 0,
    showClose: true,
  });
};

/** Helper to build a message listing other users viewing the form */
const buildOtherUsersMessage = (otherUserNames: string[]): string => {
  if (otherUserNames.length === 1) {
    return `${otherUserNames[0]} is viewing this form`;
  } else if (otherUserNames.length === 2) {
    return `${otherUserNames[0]} and ${otherUserNames[1]} are viewing this form`;
  } else {
    const names = [...otherUserNames];
    const last = names.pop();
    return `${names.join(", ")} and ${last} are viewing this form`;
  }
};

/** Sends a request to the current editor to transfer control */
const handleRequestControl = async () => {
  if (!formDesignerHub || controlRequestPending.value) return;
  controlRequestPending.value = true;
  try {
    await formDesignerHub.invoke("RequestFormControl", form.value.guid);
  } catch (error) {
    controlRequestPending.value = false;
    showError("Failed to send control request", error);
  }
};

/** Current editor receives a control request from another user */
const handleControlRequestReceived = async (request: any) => {
  try {
    await ElMessageBox.confirm(
      `${request.requestingUserName} is requesting edit control of this form. Grant access?`,
      "Control Request",
      {
        confirmButtonText: "Grant",
        cancelButtonText: "Deny",
        type: "info",
      },
    );
    // Approved
    await formDesignerHub?.invoke(
      "RespondToControlRequest",
      form.value.guid,
      request.requestingConnectionId,
      true,
    );
  } catch {
    // Denied (or dialog cancelled)
    await formDesignerHub?.invoke(
      "RespondToControlRequest",
      form.value.guid,
      request.requestingConnectionId,
      false,
    );
  }
};

/** Requester receives the response to their control request */
const handleControlRequestResponse = (response: any) => {
  controlRequestPending.value = false;
  if (response.approved) {
    ElMessage.success("Control request approved");
  } else {
    ElMessage.info(`Control request denied by ${response.fromUserName}`);
  }
};

const showTipsDialog = ref<boolean>(false);
const handleKeyDown = (e: KeyboardEvent) => {
  if (e.ctrlKey || e.altKey) {
    return;
  }
  const element = e.target as HTMLElement;
  const tagName = element.tagName;
  const normalView = tagName === "BODY" || tagName === "BUTTON";

  switch (e.key) {
    case "/":
    case "?":
      if (normalView) {
        showTipsDialog.value = !showTipsDialog.value;
      }
      break;
    case "q":
      if (normalView) {
        showQuestionCodes.value = !showQuestionCodes.value;
      }
      break;
    case "r":
      if (normalView) {
        isRuleDrawerOpen.value = !isRuleDrawerOpen.value;
      }
      break;
    case "Escape":
      // close the properties editor when the escape key is pressed
      editingComponent.value = undefined;
      break;
  }
};

// const handleDownloadFormJson = () => {
//   const element = document.createElement("a");
//   const file = new Blob([JSON.stringify(form.value, null, 2)], {
//     type: "text/plain",
//   });
//   element.href = URL.createObjectURL(file);
//   element.download = `${form.value.name}.json`;
//   document.body.appendChild(element); // Required for this to work in FireFox
//   element.click();
// };
// const handleUploadFormJson = () => {
//   uploaderOpenCounter.value++;
//   formUploaderVisible.value = true;
// };

const pendingComponent = ref<FormElementBase | null>(null);
const handlePendingComponent = (
  component: FormElementDefault<FormElementBase>,
) => {
  // Create a fresh copy of the component to avoid carrying over properties from previous components
  pendingComponent.value = JSON.parse(JSON.stringify(component));
  canDropInRepeater.value = component.type !== FormElementType.REPEATER;
};
</script>

<template>
  <div class="form-designer-page">
    <div class="form-designer-buttons">
      <el-button
        v-if="hasSetupBasicRole && canEditForm"
        class="cancelButton"
        :disabled="!saveNeeded"
        @click="handleCancelChanges"
        >Cancel
      </el-button>
      <el-button
        v-if="hasSetupBasicRole && canEditForm"
        class="saveButton"
        :disabled="!saveNeeded"
        :class="{ flash: saveNeeded }"
        type="primary"
        @click="handleSaveChanges"
        >Save
      </el-button>
    </div>
    <el-tabs v-model="activeTab" type="card" width="100%">
      <el-tab-pane label="Form Properties" name="properties">
        <div class="properties">
          <FormPropertiesEditor
            class="formPropertiesEditor"
            :model-value="form"
            :compliance-item="complianceItem"
            :workflow-definition="props.workflowDefinition"
            :read-only="!hasSetupBasicRole || !canEditForm"
            @update:model-value="handleUpdateFormProperties"
            @close-drawer="emit('closeDrawer')"
            @deleted="emit('deleted', $event)"
          />
          <!-- <div class="buttons">
            <el-button
              :disabled="!hasSetupBasicRole || !canEditForm"
              @click="handleUploadFormJson"
            >
              Upload Form JSON
            </el-button>
            <el-button @click="handleDownloadFormJson">
              Download Form JSON
            </el-button>
          </div> -->
          <el-button
            v-if="form.formType !== FormType.WORKFLOW_DEFINITION_MAIN_FORM"
            class="deleteFormButton"
            plain
            :disabled="!hasSetupBasicRole || !canEditForm"
            type="danger"
            @click="handleDeleteFormAndResponses(form.guid)"
          >
            Delete Form & All Responses
          </el-button>
        </div>
      </el-tab-pane>
      <el-tab-pane label="Design" name="design">
        <div
          class="designer-section form-designer"
          :style="{
            height:
              !props.workflowDefinition && !props.complianceItem ? '100vh' : '',
          }"
        >
          <div class="formRendererWrapper">
            <div class="formRenderer">
              <FormRendererInner
                :key="lastSaveTime.toString()"
                v-model="form"
                v-model:page-number="currentPageNumber"
                :split-pages="splitFormPages"
                :disable-rules-engine="!applyRules"
                @update:form-pages="(val: FormPage[]) => (formPages = val)"
              >
                <template
                  #component="{ component }: { component: FormElementBase }"
                >
                  <div
                    v-if="!component.isArchived || showArchived"
                    :draggable="hasSetupBasicRole && canEditForm"
                    class="component-wrapper"
                    @click="() => handleClickComponent(component)"
                    @dragstart="
                      hasSetupBasicRole &&
                      canEditForm &&
                      handleOnDrag($event, component)
                    "
                    @dragend="
                      hasSetupBasicRole &&
                      canEditForm &&
                      handleOnDrag($event, component)
                    "
                  >
                    <DroppablePad
                      :id="component.guid || component.questionCode"
                      :key="component.guid"
                      :is-dragging="isDragging"
                      @drop="handleDropComponent"
                    >
                      <FormComponentRenderer
                        :model-value="component"
                        :show-question-codes="
                          showQuestionCodes || isRuleDrawerOpen
                        "
                        :display-mode="
                          !hasSetupBasicRole || !canEditForm
                            ? FormDisplayMode.READ_ONLY
                            : undefined
                        "
                        :show-archived="showArchived"
                        :class="{
                          highlight: editingComponent === component,
                          'section-selected':
                            editingComponent?.guid === component.parentGuid,
                          [component.questionCode]: true,
                        }"
                      />
                    </DroppablePad>
                  </div>
                </template>
              </FormRendererInner>
            </div>
          </div>
          <div class="sidePanelWrapper">
            <section class="sidePanel">
              <div class="formTopOptions">
                <div class="formButtons">
                  <el-button
                    :disabled="!hasSetupBasicRole || !canEditForm"
                    @click="handleOpenRuleDrawer"
                    >Edit Rules</el-button
                  >
                  <el-button
                    v-if="
                      concurrencyMode === FormConcurrencyMode.REQUEST_CONTROL &&
                      hasSetupBasicRole &&
                      !canEditForm
                    "
                    :loading="controlRequestPending"
                    type="warning"
                    @click="handleRequestControl"
                    >Request Control</el-button
                  >
                </div>

                <div class="table-of-contents">
                  <el-popover
                    trigger="hover"
                    placement="bottom"
                    width="auto"
                    :popper-style="{ maxHeight: '75vh', overflow: 'auto' }"
                  >
                    <template #reference>
                      <el-button
                        circle
                        :icon="Expand"
                        title="Table of Contents"
                      />
                    </template>
                    <TableOfContents
                      :form-pages="formPages"
                      :form="form"
                      @switch-page="(val: number) => (currentPageNumber = val)"
                      @handle-select-menu-item="handleClickComponent"
                    />
                  </el-popover>
                </div>

                <div class="formOptionButtons">
                  <el-checkbox
                    v-model="showQuestionCodes"
                    label="Show Question Codes"
                    title="Press q to toggle"
                  />
                  <el-checkbox
                    v-model="autoSaveForm"
                    label="Autosave Form"
                    :disabled="!hasSetupBasicRole || !canEditForm"
                  />
                  <el-checkbox
                    v-model="splitFormPages"
                    label="Split Pages"
                    :disabled="!hasSetupBasicRole || !canEditForm"
                  />
                  <el-checkbox
                    v-model="applyRules"
                    label="Apply Rules"
                    :disabled="!hasSetupBasicRole || !canEditForm"
                  />
                  <el-checkbox
                    v-if="numberArchived"
                    v-model="showArchived"
                    :label="`Show Archived (${numberArchived})`"
                  />
                </div>
              </div>

              <ElementEditor
                v-if="editingComponent"
                :key="_editingComponentGuid"
                v-model:form="form"
                v-model="editingComponent"
                class="elementPropertiesEditor"
                :can-delete="
                  !elementsThatCantBeDeleted.includes(editingComponent.guid) &&
                  !editingComponent.isArchived
                "
                :section-assignables="sectionAssignables"
                :save-needed="saveNeeded"
                :workflow-definition="props.workflowDefinition"
                :active-tab="activeElementEditorTab"
                :responses-exist="responsesExist"
                :entity-definitions-list="entityDefinitions"
                :form-definitions-list="formDefinitionsList"
                :list-definitions-list="listDefinitionsList"
                :event-codes-list="eventCodesList"
                :security-groups-list="securityGroups"
                :read-only="!hasSetupBasicRole || !canEditForm"
                @delete:form-element="handleDeleteElementFromWorkflow"
                @archive:form-element="handleArchiveElementFromWorkflow"
                @unarchive:form-element="handleUnarchiveElementFromWorkflow"
                @stop-editing:form-element="handleStopEditing"
                @update:active-tab="
                  (val: string) => (activeElementEditorTab = val)
                "
              />

              <CompliancePalette
                v-if="form.formType !== FormType.COMPLIANCE_ITEM_DEFINITION"
                v-model:is-dragging="isDragging"
                :form-components="form.components"
                :form="form"
                :read-only="!hasSetupBasicRole || !canEditForm"
                @component="handlePendingComponent"
              />
              <FormDesignerPalette
                v-model:is-dragging="isDragging"
                :read-only="!hasSetupBasicRole || !canEditForm"
                @component="handlePendingComponent"
              />
            </section>
          </div>
          <el-icon
            class="pageTipIcon"
            title="View Tips"
            @click="showTipsDialog = true"
          >
            <el-icon-question-filled />
          </el-icon>
        </div>
      </el-tab-pane>

      <el-tab-pane label="Access" name="restrictions">
        <FormRule
          v-model:form="form"
          :rule-type="FormRuleType.IS_RESTRICTED_TO"
          :read-only="!hasSetupBasicRole || !canEditForm"
          :assignables="sectionAssignables"
        />
      </el-tab-pane>
      <el-tab-pane label="Lists" name="lists" lazy>
        <ListsTab
          v-if="form.guid && props.workflowDefinition?.guid"
          :form-definition-guid="form.guid"
          :workflow-definition-guid="props.workflowDefinition?.guid"
          :readonly="!myInfoStore.hasSetupItemsRole"
        />
      </el-tab-pane>
      <!-- <el-tab-pane label="Reviews" name="reviews">
        <FormRule v-model:form="form" :rule-type="FormRuleType.REQUIRES_REVIEW" :assignables="sectionAssignables" />
      </el-tab-pane> -->
      <!-- <el-tab-pane label="Approvals" name="approvals">
        <FormRule v-model:form="form" :rule-type="FormRuleType.REQUIRES_APPROVAL" :assignables="sectionAssignables" />
      </el-tab-pane> -->
    </el-tabs>

    <FormJsonUploader
      :key="uploaderOpenCounter"
      v-model="formUploaderVisible"
      :form-definition-guid="form.guid"
      @update:form="
        ($event: FormDefinition) => {
          form = $event;
          lastSaveTime = new Date();
        }
      "
    />
    <el-drawer
      v-model="isRuleDrawerOpen"
      title="Edit Rules"
      direction="rtl"
      modal-class="drawer-modal"
      :size="drawerWidth"
      @closed="handleCloseRuleDrawer"
    >
      <FormRulesTable
        v-model="form"
        :workflow-definition="props.workflowDefinition"
        @close-rule-drawer="handleCloseRuleDrawer"
      />
    </el-drawer>

    <el-dialog
      ref="tipsDialog"
      v-model="showTipsDialog"
      class="tipsDialog"
      :append-to-body="true"
      title="Keyboard Tips"
      :close-on-click-modal="false"
      :close-on-press-escape="false"
    >
      <table>
        <thead>
          <tr>
            <th colspan="2">General</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td class="left"><span class="key">Esc</span> :</td>
            <td class="right">Close "Properties Panel"</td>
          </tr>
          <tr>
            <td class="left"><span class="key">q</span> :</td>
            <td class="right">Toggle "Show Question Code"</td>
          </tr>
          <tr>
            <td class="left"><span class="key">r</span> :</td>
            <td class="right">Open "Edit Rules" panel</td>
          </tr>
          <tr>
            <td class="left">
              <span class="key">/</span> or <span class="key">?</span> :
            </td>
            <td class="right">Show/hide these tips.</td>
          </tr>
        </tbody>
      </table>
    </el-dialog>
  </div>
</template>

<style lang="less">
.el-drawer__body {
  overflow-y: auto;
}
.el-drawer__header {
  margin-bottom: 0;
}
.el-drawer__title {
  font-size: 2rem;
  color: black;
}
.el-drawer__body,
.form-designer-page,
.formContainer,
.form-designer {
  height: 100%;
}
.form-designer-page {
  // display: grid;

  .form-designer-buttons {
    display: flex;
    gap: 10px;

    .saveButton {
      position: fixed;
      right: 20px;
      z-index: 100;
    }
    .cancelButton {
      position: fixed;
      right: 100px;
      z-index: 100;
    }
  }

  .form-designer {
    display: grid;
    grid-template-columns: minmax(350px, 1fr) minmax(250px, 1fr);
    grid-template-areas: "form sidePanel";
    column-gap: 20px;
    row-gap: 8px;
    max-width: 1800px;

    .sidePanelWrapper {
      overflow-y: auto;
    }

    .sidePanel {
      grid-area: sidePanel;
      position: sticky;
      top: 0;
      height: min-content;
      // background-color: red;
      gap: 16px;

      // for the panels on the side
      > div {
        height: min-content;
        box-shadow: inset 0px 0px 4px #666;
        border-radius: var(--el-border-radius-base);
        display: flex;
        flex-direction: column;

        & + div {
          margin-top: 1em;
        }
      }
      .elementPropertiesEditor {
        background-color: var(--el-color-success-light-9);
      }
    }

    .tipsDialog {
      table {
        width: 100%;
        th {
          font-size: 1.2em;
          padding: 0.5em 0;
        }
        td {
          padding: 0.5em 0;
          .key {
            font-weight: bold;
            font-size: 0.1em;
          }
        }
        .left {
          text-align: right;
        }
      }
    }

    .pageTipIcon {
      position: fixed;
      bottom: 10px;
      right: 10px;
      font-size: 24px;
      cursor: pointer;
    }

    .formRendererWrapper {
      overflow-y: auto;

      .formRenderer {
        grid-area: form;
        display: block;
        width: 100%;
      }
    }

    div.formTopOptions {
      display: grid;
      border: none;
      box-shadow: none;
      grid-template-areas: "buttons toc options";
      grid-template-columns: 1fr auto auto;
      align-items: baseline;
      gap: 10px;

      .el-button {
        margin: 0;
      }
      .table-of-contents {
        grid-area: toc;
        overflow: hidden;
        white-space: nowrap;
        text-overflow: ellipsis;
        width: 100%;
      }
      .formButtons {
        grid-area: buttons;
        display: flex;
        flex-wrap: wrap;
        gap: 4px;
      }

      .formOptionButtons {
        grid-area: options;
        display: flex;
        flex-wrap: wrap;
        column-gap: 10px;
        padding-left: 15px;
        .el-checkbox {
          color: var(--el-text-color-secondary);
          margin-right: 0;
          .el-checkbox__label {
            white-space: normal;
          }
        }
      }

      @keyframes flash {
        0% {
          background-color: #409eff; /* default primary color */
        }
        50% {
          background-color: #66b1ff; /* lighter primary color */
        }
        100% {
          background-color: #409eff;
        }
      }

      .flash {
        animation: flash 1s infinite;
      }
    }

    .designer-section {
      width: 100%;
    }

    .designable-settings-drawer {
      position: fixed;
      z-index: 100;
      bottom: 0px;
      left: 50%;
      transform: translateX(-50%);
      width: 1000px;
      // background: #fff;
      margin: 0px auto;
      border-top-left-radius: 10px;
      border-top-right-radius: 10px;
      border: 2px solid var(--ep-color-primary);
    }

    h2 {
      font-size: 20px;
      margin: 0 0 10px 10px;

      div {
        font-weight: normal;
        font-size: small;
        line-height: 0.9;
        color: grey;
      }
    }
    .component-wrapper {
      position: relative;
      padding: 3px 0;
      display: flex;
      flex-direction: column;
      cursor: pointer;

      &:hover {
        background-color: #ffff8766;
      }

      .highlight {
        background-color: #e1f7d6;
      }

      .section-selected {
        background-color: #f0f9eb;
      }
    }
  }
  .properties {
    .formPropertiesEditor {
      padding: 1em 0em;
    }

    .buttons {
      margin-left: 50px;
    }
    .deleteFormButton {
      margin-top: 3em;
      margin-left: 50px;
    }
  }
  .inPdf,
  .notInPdf {
    display: unset;
    background-color: #f1f1f1;
  }
  .inPdf::before {
    content: "";
    display: inline-block; /* Ensure the pseudo-element behaves like an inline element */
    width: 16px; /* Adjust to your icon's width */
    height: 16px; /* Adjust to your icon's height */
    background-image: url("/images/pdf.png"); /* Path to your icon */
    background-size: contain; /* Ensure the icon fits within the dimensions */
    background-repeat: no-repeat;
    vertical-align: middle; /* Align the icon vertically */
  }
  .notInPdf::before {
    content: "X"; /* Empty content to avoid text */
    color: red;
    font-size: 16px;
    line-height: 16px;
    text-align: center;
    display: inline-block; /* Ensure the pseudo-element behaves like an inline element */
    width: 16px; /* Adjust to your icon's width */
    height: 16px; /* Adjust to your icon's height */
    background-image: url("/images/pdf.png"); /* Path to your icon */
    background-size: contain; /* Ensure the icon fits within the dimensions */
    background-repeat: no-repeat;
    vertical-align: middle; /* Align the icon vertically */
  }
}
.el-overlay.drawer-modal {
  background-color: rgba(0, 0, 0, 0.2);
}
</style>
